import React, { useState, useCallback, useMemo } from "react";
import { Post } from "@/core/types/postTypes";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { PostMediaSection } from "@/components/PostCard/PostMediaSection";
import { PostBadge } from "@/components/PostCard";
import { VideoPlayer } from "@/components/ui/VideoPlayer";
import { UserRole } from '@/services/urlService';

interface PostMediaPreviewProps {
  post: Post;
  clientId: string;
  className?: string;
}

export const PostMediaPreview: React.FC<PostMediaPreviewProps> = ({
  post,
  clientId,
  className = "",
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isVideoPlayerOpen, setIsVideoPlayerOpen] = useState(false);
  const [selectedVideoForPlayer, setSelectedVideoForPlayer] =
    useState<MediaItem | null>(null);

  // Convert media items to MediaItem instances with proper authentication
  const sortedMediaItems = useMemo(() => {
    if (!post?.mediaItems || post.mediaItems.length === 0) return [];

    const getAccessToken = () => {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          return user.accessToken || null;
        } catch {
          return null;
        }
      }
      return null;
    };

    const accessToken = getAccessToken();

    const instances = post.mediaItems.map((item) =>
      item instanceof MediaItem
        ? item
        : MediaItem.fromBackend(item, UserRole.ADMIN, clientId, accessToken)
    );

    return instances.sort((a, b) => {
      const posA = a.position ?? 999;
      const posB = b.position ?? 999;
      return posA - posB;
    });
  }, [post?.mediaItems, clientId]);

  const getReelVideoItem = useCallback(() => {
    if (!sortedMediaItems) return null;
    return sortedMediaItems.find((item) => item.isVideo()) || null;
  }, [sortedMediaItems]);

  const getCurrentImage = useCallback(() => {
    if (post.postType === "reel") {
      // For reels, prefer thumbnail
      if (post.thumbnailMediaItem) {
        return post.thumbnailMediaItem;
      }

      if (post.thumbnailMediaId && sortedMediaItems) {
        const thumbnailId =
          typeof post.thumbnailMediaId === "string"
            ? parseInt(post.thumbnailMediaId)
            : post.thumbnailMediaId;

        const thumbnailItem = sortedMediaItems.find(
          (item) => item.id === thumbnailId
        );
        if (thumbnailItem && thumbnailItem.isImage()) {
          return thumbnailItem;
        }
      }

      if (sortedMediaItems) {
        const imageItem = sortedMediaItems.find((item) => item.isImage());
        if (imageItem) {
          return imageItem;
        }
      }

      const videoItem = getReelVideoItem();
      if (videoItem) {
        return videoItem;
      }
    }

    if (!sortedMediaItems || sortedMediaItems.length === 0) return null;

    if (
      post.isCarousel &&
      sortedMediaItems.length > 1 &&
      post.postType !== "reel"
    ) {
      const imageIndex = Math.min(currentImageIndex, sortedMediaItems.length - 1);
      return sortedMediaItems[imageIndex];
    }

    return sortedMediaItems[0];
  }, [
    sortedMediaItems,
    post.isCarousel,
    post.postType,
    post.thumbnailMediaItem,
    post.thumbnailMediaId,
    currentImageIndex,
    getReelVideoItem,
  ]);

  const handleVideoClick = useCallback((video: MediaItem) => {
    setSelectedVideoForPlayer(video);
    setIsVideoPlayerOpen(true);
  }, []);

  const handleVideoPlayerClose = useCallback(() => {
    setIsVideoPlayerOpen(false);
    setSelectedVideoForPlayer(null);
  }, []);

  const getStreamUrlByFileId = useCallback(
    (fileId: string): string | undefined => {
      if (!clientId) {
        return undefined;
      }

      const mediaItem = sortedMediaItems.find(
        (item) => item.fileIdentity === fileId
      );

      if (!mediaItem) {
        console.warn(`Media item not found for fileId: ${fileId}`);
        return undefined;
      }

      return mediaItem.getStreamUrlWithQueryAuth();
    },
    [clientId, sortedMediaItems]
  );

  const handleImageClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();

      if (post.postType === "reel") {
        const videoItem = getReelVideoItem();
        if (videoItem) {
          handleVideoClick(videoItem);
        }
      } else if (post.isCarousel && sortedMediaItems && sortedMediaItems.length > 1) {
        setCurrentImageIndex((prevIndex) => {
          return (prevIndex + 1) % sortedMediaItems.length;
        });
      }
    },
    [post.isCarousel, post.postType, sortedMediaItems, getReelVideoItem, handleVideoClick]
  );

  const currentImage = getCurrentImage();
  const imageCount = sortedMediaItems?.length || 0;

  if (!currentImage) {
    return (
      <div className={`bg-gray-100 rounded-lg flex items-center justify-center ${className}`}>
        <div className="text-center text-gray-500">
          <svg
            className="w-12 h-12 mx-auto mb-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <p className="text-sm">No media available</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className={`relative ${className}`}
        onClick={handleImageClick}
        style={{ cursor: post.isCarousel || post.postType === "reel" ? "pointer" : "default" }}
        title={
          post.postType === "reel"
            ? "Click to play reel"
            : post.isCarousel && imageCount > 1
            ? `Click to cycle through images (${currentImageIndex + 1}/${imageCount})`
            : undefined
        }
      >
        <PostMediaSection
          mediaItem={currentImage}
          viewMode="grid"
          isReel={post.postType === "reel"}
          isCarousel={post.isCarousel}
        />

        {/* Carousel indicator */}
        {post.postType !== "reel" && post.isCarousel && imageCount > 1 && (
          <div className="absolute top-2 right-2">
            <PostBadge.CarouselCountBadge
              currentIndex={currentImageIndex}
              totalItems={imageCount}
            />
          </div>
        )}

        {/* Carousel dots */}
        {post.isCarousel && imageCount > 1 && (
          <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1">
            {sortedMediaItems.slice(0, 10).map((_, index) => (
              <div
                key={index}
                className={`w-1.5 h-1.5 rounded-full transition-all ${
                  index === currentImageIndex
                    ? "bg-white w-3"
                    : "bg-white/60"
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Video Player Modal */}
      {selectedVideoForPlayer && (
        <VideoPlayer
          mediaItem={selectedVideoForPlayer}
          isOpen={isVideoPlayerOpen}
          onClose={handleVideoPlayerClose}
          clientId={clientId}
          getStreamUrlByFileId={getStreamUrlByFileId}
        />
      )}
    </>
  );
};
